import { mockProducts } from '../mock-data';

/**
 * @typedef {import('../types').Product} Product
 * @typedef {import('../types').ProductFilter} ProductFilter
 * 
 * @typedef {Object} SortConfig
 * @property {keyof Product} field - Field to sort by
 * @property {'asc' | 'desc'} direction - Sort direction
 */

const API_BASE_URL = '/api/products';

/**
 * Fetch products with optional filters and sorting
 * @param {ProductFilter} [filters] - Optional filters to apply
 * @param {SortConfig} [sort] - Optional sorting configuration
 * @returns {Promise<Product[]>}
 */
export async function fetchProducts(filters, sort) {
  // In development, return mock data
  if (process.env.NODE_ENV === 'development') {
    // Filter products based on the filters
    let filteredProducts = [...mockProducts];

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filteredProducts = filteredProducts.filter(product => 
        product.name.toLowerCase().includes(searchLower) ||
        product.sku.toLowerCase().includes(searchLower) ||
        product.category.toLowerCase().includes(searchLower)
      );
    }

    if (filters?.category) {
      filteredProducts = filteredProducts.filter(product => 
        product.category === filters.category
      );
    }

    if (filters?.status) {
      filteredProducts = filteredProducts.filter(product => 
        product.status === filters.status
      );
    }

    if (filters?.reCommerceFlags) {
      const flags = filters.reCommerceFlags;
      filteredProducts = filteredProducts.filter(product => {
        const attrs = product.reCommerceAttributes;
        return (!flags.eligibleForResale || attrs.eligibleForResale) &&
               (!flags.eligibleForRepair || attrs.eligibleForRepair) &&
               (!flags.eligibleForRental || attrs.eligibleForRental) &&
               (!flags.isRefillable || attrs.isRefillable);
      });
    }

    // Sort products if sort config is provided
    if (sort) {
      filteredProducts.sort((a, b) => {
        const aValue = a[sort.field];
        const bValue = b[sort.field];
        const modifier = sort.direction === 'asc' ? 1 : -1;

        if (typeof aValue === 'string') {
          return aValue.localeCompare(bValue) * modifier;
        }
        return (aValue - bValue) * modifier;
      });
    }

    return Promise.resolve(filteredProducts);
  }

  const queryParams = new URLSearchParams();

  // Add filters to query params
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value.toString());
      }
    });
  }

  // Add sort to query params
  if (sort) {
    queryParams.append('sortField', sort.field);
    queryParams.append('sortDirection', sort.direction);
  }

  const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to fetch products');
  }
  return response.json();
}

/**
 * Fetch a single product by ID
 * @param {string} id - Product ID
 * @returns {Promise<Product>}
 */
export async function fetchProduct(id) {
  // In development, return mock data
  if (process.env.NODE_ENV === 'development') {
    const product = mockProducts.find(p => p.id === id);
    if (!product) {
      throw new Error('Product not found');
    }
    return Promise.resolve(product);
  }

  const response = await fetch(`${API_BASE_URL}/${id}`);
  if (!response.ok) {
    throw new Error('Failed to fetch product');
  }
  return response.json();
}

/**
 * Create a new product
 * @param {Omit<Product, 'id'>} product - Product data without ID
 * @returns {Promise<Product>}
 */
export async function createProduct(product) {
  const response = await fetch(`${API_BASE_URL}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(product),
  });
  if (!response.ok) {
    throw new Error('Failed to create product');
  }
  return response.json();
}

/**
 * Update an existing product
 * @param {string} id - Product ID
 * @param {Partial<Product>} product - Partial product data to update
 * @returns {Promise<Product>}
 */
export async function updateProduct(id, product) {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(product),
  });
  if (!response.ok) {
    throw new Error('Failed to update product');
  }
  return response.json();
}

/**
 * Delete a product
 * @param {string} id - Product ID
 * @returns {Promise<void>}
 */
export async function deleteProduct(id) {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to delete product');
  }
}

/**
 * Upload a product image
 * @param {string} id - Product ID
 * @param {File} file - Image file to upload
 * @returns {Promise<{url: string}>}
 */
export async function uploadProductImage(id, file) {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(`${API_BASE_URL}/${id}/images`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Failed to upload image');
  }

  return response.json();
}

/**
 * Delete a product image
 * @param {string} productId - Product ID
 * @param {string} imageId - Image ID
 * @returns {Promise<void>}
 */
export async function deleteProductImage(productId, imageId) {
  const response = await fetch(`${API_BASE_URL}/${productId}/images/${imageId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete image');
  }
}

/**
 * Update product images order and primary status
 * @param {string} id - Product ID
 * @param {Array<{id: string, sortOrder: number, isPrimary: boolean}>} images - Updated image data
 * @returns {Promise<void>}
 */
export async function updateProductImages(id, images) {
  const response = await fetch(`${API_BASE_URL}/${id}/images/order`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ images }),
  });

  if (!response.ok) {
    throw new Error('Failed to update image order');
  }
}

/**
 * Export products to CSV
 * @returns {Promise<void>}
 */
export async function exportProducts() {
  const response = await fetch(`${API_BASE_URL}/export`);
  if (!response.ok) {
    throw new Error('Failed to export products');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'products.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Import products from CSV file
 * @param {File} file - CSV file to import
 * @returns {Promise<void>}
 */
export async function importProducts(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/import`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    throw new Error('Failed to import products');
  }
} 